/**
 * Glowwa PDP — one-time (no subscription) buy area.
 * Price sync on variant change; quantity dropdown syncs to Horizon quantity input.
 */
(function glowwaOnetimeBuy() {
  const PRICE_SELECTOR = '[data-glowwa-onetime-price]';
  const QTY_SELECT_SELECTOR = '[data-glowwa-onetime-quantity-select]';
  const ROOT_SELECTOR = '.glowwa-pdp-buy-area--onetime';

  /**
   * @param {HTMLElement} root
   * @returns {HTMLSelectElement | null}
   */
  function getQuantitySelect(root) {
    const el = root.querySelector(QTY_SELECT_SELECTOR);
    return el instanceof HTMLSelectElement ? el : null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLInputElement | null}
   */
  function getQuantityInput(root) {
    const el = root.querySelector(
      'quantity-selector-component input[ref="quantityInput"], quantity-selector-component input[name="quantity"]',
    );
    return el instanceof HTMLInputElement ? el : null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  function getQuantityComponent(root) {
    const el = root.querySelector('quantity-selector-component');
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * @param {HTMLInputElement} input
   * @param {HTMLSelectElement} select
   */
  function readConstraints(input, select) {
    const min = Number.parseInt(input.min, 10) || Number.parseInt(select.dataset.min, 10) || 1;
    const step = Number.parseInt(input.step, 10) || Number.parseInt(select.dataset.step, 10) || 1;
    const attrMax = input.max ? Number.parseInt(input.max, 10) : null;
    const fallback = Number.parseInt(select.dataset.maxFallback, 10) || 10;
    const cap = attrMax != null ? attrMax : fallback;

    return { min, step, cap, attrMax };
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {HTMLInputElement} input
   */
  function populateSelectOptions(select, input) {
    const { min, step, cap } = readConstraints(input, select);
    const current = Number.parseInt(input.value, 10) || min;
    const previous = select.value;

    select.replaceChildren();

    for (let qty = min, count = 0; qty <= cap && count < 50; qty += step, count += 1) {
      const option = document.createElement('option');
      option.value = String(qty);
      option.textContent = String(qty);
      if (qty === current) {
        option.selected = true;
      }
      select.appendChild(option);
    }

    if (![...select.options].some((opt) => opt.value === String(current))) {
      const option = document.createElement('option');
      option.value = String(current);
      option.textContent = String(current);
      option.selected = true;
      select.appendChild(option);
    }

    if ([...select.options].some((opt) => opt.value === previous)) {
      select.value = previous;
    }
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {HTMLInputElement} input
   * @param {HTMLElement | null} component
   */
  function syncSelectToInput(select, input, component) {
    const value = select.value;
    if (!value) {
      return;
    }

    input.value = value;

    if (typeof component?.onQuantityChange === 'function') {
      component.onQuantityChange();
    } else {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (typeof component?.updateButtonStates === 'function') {
      component.updateButtonStates();
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function bindQuantityDropdown(root) {
    const select = getQuantitySelect(root);
    const input = getQuantityInput(root);

    if (!select || !input) {
      return;
    }

    const component = getQuantityComponent(root);
    populateSelectOptions(select, input);

    if (root.dataset.glowwaQtyBound === 'true') {
      return;
    }

    root.dataset.glowwaQtyBound = 'true';

    root.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLSelectElement)) {
        return;
      }
      if (!target.matches(QTY_SELECT_SELECTOR)) {
        return;
      }

      const activeInput = getQuantityInput(root);
      const activeComponent = getQuantityComponent(root);
      if (activeInput) {
        syncSelectToInput(target, activeInput, activeComponent);
      }
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function refreshQuantityDropdown(root) {
    const select = getQuantitySelect(root);
    const input = getQuantityInput(root);
    if (!select || !input) {
      return;
    }

    populateSelectOptions(select, input);
    select.value = input.value || select.value;
  }

  /**
   * @param {HTMLElement} root
   * @param {CustomEvent} event
   */
  function syncPrice(root, event) {
    const priceEl = root.querySelector(PRICE_SELECTOR);
    if (!(priceEl instanceof HTMLElement)) {
      return;
    }

    const productId = root.getAttribute('data-product-id');
    const eventProductId = event.detail?.data?.productId;

    if (productId && eventProductId && productId !== String(eventProductId)) {
      return;
    }

    const html = event.detail?.data?.html;
    if (html instanceof Document) {
      const nextPrice = html.querySelector(PRICE_SELECTOR);
      if (nextPrice instanceof HTMLElement) {
        priceEl.innerHTML = nextPrice.innerHTML;
      }
    } else {
      const variant = event.detail?.resource;
      if (variant?.price != null && typeof window.Shopify?.formatMoney === 'function') {
        const showCompare =
          priceEl.dataset.glowwaShowCompare === 'true' &&
          variant.compare_at_price != null &&
          variant.compare_at_price > variant.price;

        const sale = document.createElement('span');
        sale.className = 'price glowwa-onetime-checkout__sale-price';
        sale.textContent = window.Shopify.formatMoney(variant.price);

        if (showCompare) {
          const compare = document.createElement('span');
          compare.className = 'compare-at-price glowwa-onetime-checkout__compare-price';
          compare.textContent = window.Shopify.formatMoney(variant.compare_at_price);
          priceEl.replaceChildren(compare, sale);
        } else {
          priceEl.replaceChildren(sale);
        }
      }
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function enhance(root) {
    bindQuantityDropdown(root);
  }

  /**
   * @param {ParentNode} [scope]
   */
  function enhanceAll(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root instanceof HTMLElement) {
        enhance(root);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    enhanceAll();
  });

  document.addEventListener('variant:update', (event) => {
    if (!(event instanceof CustomEvent)) {
      return;
    }

    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!(root instanceof HTMLElement)) {
        return;
      }

      syncPrice(root, event);

      requestAnimationFrame(() => {
        refreshQuantityDropdown(root);
      });
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target instanceof HTMLElement) {
      enhanceAll(target);
    }
  });
})();
